import { WARM_UP_MS } from '../constants';
import type {
  ChallengeType,
  ChallengeStatus,
  ChallengeResult,
  FrameResult,
} from '../types';

export abstract class BaseChallenge {
  protected challengeId: string;

  protected type: ChallengeType;

  protected status: ChallengeStatus = 'pending';

  protected startedAt = '';

  protected completedAt = '';

  protected framesCaptured = 0;

  protected confidence = 0;

  private timeoutId: ReturnType<typeof setTimeout> | null = null;

  private warmUpId: ReturnType<typeof setTimeout> | null = null;

  private isWarmingUp = true;

  onPass?: (result: ChallengeResult) => void;

  onFail?: (result: ChallengeResult) => void;

  onTimeout?: (result: ChallengeResult) => void;

  onProgress?: (progress: number) => void;

  onWarmUpEnd?: () => void;

  constructor(type: ChallengeType) {
    this.type = type;
    const random = Math.random().toString(36).slice(2);
    this.challengeId = `${type}-${Date.now()}-${random}`;
  }

  abstract processFrame(frame: FrameResult): void;

  validateFrame(frame: FrameResult): void {
    if (this.isWarmingUp || this.status !== 'active') return;
    this.processFrame(frame);
  }

  start(timeoutMs: number): void {
    this.status = 'active';
    this.startedAt = new Date().toISOString();
    this.framesCaptured = 0;
    this.confidence = 0;
    this.isWarmingUp = true;

    this.warmUpId = setTimeout(() => {
      this.warmUpId = null;
      this.isWarmingUp = false;
      if (this.onWarmUpEnd) {
        this.onWarmUpEnd();
      }
      this.timeoutId = setTimeout(() => {
        this.timeoutChallenge();
      }, timeoutMs);
    }, WARM_UP_MS);
  }

  protected pass(confidence: number): void {
    if (this.status !== 'active') return;

    this.confidence = Math.min(confidence, 1);
    this.status = 'passed';
    this.completedAt = new Date().toISOString();
    this.clearTimeout();

    if (this.onPass) {
      this.onPass(this.getResult());
    }
  }

  protected fail(): void {
    if (this.status !== 'active') return;

    this.status = 'failed';
    this.completedAt = new Date().toISOString();
    this.clearTimeout();

    if (this.onFail) {
      this.onFail(this.getResult());
    }
  }

  private timeoutChallenge(): void {
    if (this.status !== 'active') return;

    this.status = 'timeout';
    this.completedAt = new Date().toISOString();

    if (this.onTimeout) {
      this.onTimeout(this.getResult());
    }
  }

  getResult(): ChallengeResult {
    return {
      challengeId: this.challengeId,
      type: this.type,
      status: this.status,
      startedAt: this.startedAt,
      completedAt: this.completedAt,
      durationMs:
        new Date(this.completedAt).getTime() -
        new Date(this.startedAt).getTime(),
      confidence: this.confidence,
      framesCaptured: this.framesCaptured,
    };
  }

  protected captureFrame(): void {
    this.framesCaptured++;
  }

  protected reportProgress(progress: number): void {
    if (this.onProgress) {
      this.onProgress(Math.min(Math.max(progress, 0), 1));
    }
  }

  private clearTimeout(): void {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (this.warmUpId !== null) {
      clearTimeout(this.warmUpId);
      this.warmUpId = null;
    }
  }
}
